<template>
  <canvas
    ref="canvas"
    :class="{ 'gx-canvas': true, 'gx-canvas--fixed': fixed }"
    :style="{ opacity: opacity }"
  ></canvas>
</template>

<script>
/**
 * 星空 + 流星 Canvas，还原 merchant-new-ui 各页面的背景效果
 * props: count 星星数量, meteorMax 同时流星上限, meteorGap 流星间隔(帧), opacity,
 *        fixed 是否 fixed 定位, layer z-index 通过 style 传入
 */
export default {
  name: 'StarsCanvas',
  props: {
    count: { type: Number, default: 100 },
    meteorMax: { type: Number, default: 2 },
    meteorGap: { type: Number, default: 900 },
    opacity: { type: Number, default: 1 },
    fixed: { type: Boolean, default: false },
    starMaxAlpha: { type: Number, default: 0.75 },
    starColors: {
      type: Array,
      default: () => ['#ffffff', '#ffffff', '#ffffff', '#F0F4FF', '#D4A946', '#E8C778', '#3FB6E0']
    }
  },
  data() {
    return {
      raf: 0,
      W: 0,
      H: 0,
      stars: [],
      meteors: [],
      mt: 0,
      reducedMotion: false
    }
  },
  mounted() {
    this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.resize()
    window.addEventListener('resize', this.resize)
    this.raf = requestAnimationFrame(this.draw)
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf)
    window.removeEventListener('resize', this.resize)
  },
  methods: {
    resize() {
      const c = this.$refs.canvas
      if (!c) return
      const ctx = c.getContext('2d')
      if (!ctx) return
      this.W = c.offsetWidth
      this.H = c.offsetHeight
      c.width = this.W * window.devicePixelRatio
      c.height = this.H * window.devicePixelRatio
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
      const colors = this.starColors.length ? this.starColors : ['#ffffff']
      this.stars = Array.from({ length: this.count }, () => ({
        x: Math.random() * this.W,
        y: Math.random() * this.H,
        r: 0.3 + Math.random() * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        p: Math.random() * Math.PI * 2,
        sp: (1.5 + Math.random() * 3) * 0.001,
        max: 0.15 + Math.random() * this.starMaxAlpha
      }))
    },
    spawnMeteor() {
      const sx = this.W * 0.3 + Math.random() * this.W * 0.6
      const sy = Math.random() * this.H * 0.35
      const angle = Math.PI / 4 + Math.random() * Math.PI / 6
      const sp = 7 + Math.random() * 7
      this.meteors.push({
        x: sx, y: sy,
        vx: -Math.cos(angle) * sp,
        vy: Math.sin(angle) * sp,
        l: 130 + Math.random() * 90,
        life: 0,
        max: 55 + Math.random() * 30,
        ok: true
      })
    },
    draw(t) {
      const c = this.$refs.canvas
      if (!c) return
      const ctx = c.getContext('2d')
      ctx.clearRect(0, 0, this.W, this.H)
      this.stars.forEach((s) => {
        const o = 0.06 + (s.max - 0.06) * (0.5 + 0.5 * Math.sin(s.p + t * s.sp))
        ctx.beginPath()
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
        ctx.fillStyle = s.color
        ctx.globalAlpha = o
        ctx.fill()
      })
      ctx.globalAlpha = 1
      this.mt++
      const alive = this.meteors.filter((m) => m.ok).length
      if (this.mt >= this.meteorGap && alive < this.meteorMax) {
        this.spawnMeteor()
        this.mt = 0
      }
      this.meteors = this.meteors.filter((m) => m.ok || m.life < m.max + 4)
      this.meteors.forEach((m) => {
        if (!m.ok) return
        m.x += m.vx
        m.y += m.vy
        m.life++
        const a = Math.min(1, (m.max - m.life) / 14)
        if (a <= 0 || m.x < -80 || m.y > this.H + 40) {
          m.ok = false
          return
        }
        const tx = m.x - m.vx * (m.l / 10)
        const ty = m.y - m.vy * (m.l / 10)
        const g = ctx.createLinearGradient(m.x, m.y, tx, ty)
        g.addColorStop(0, `rgba(255,225,100,${a})`)
        g.addColorStop(0.35, `rgba(212,169,70,${a * 0.55})`)
        g.addColorStop(1, 'rgba(212,169,70,0)')
        ctx.beginPath()
        ctx.moveTo(m.x, m.y)
        ctx.lineTo(tx, ty)
        ctx.strokeStyle = g
        ctx.lineWidth = 2.5
        ctx.stroke()
        const hg = ctx.createRadialGradient(m.x, m.y, 0, m.x, m.y, 7)
        hg.addColorStop(0, `rgba(255,240,160,${a})`)
        hg.addColorStop(1, 'transparent')
        ctx.beginPath()
        ctx.arc(m.x, m.y, 7, 0, Math.PI * 2)
        ctx.fillStyle = hg
        ctx.fill()
      })
      if (!this.reducedMotion) this.raf = requestAnimationFrame(this.draw)
    }
  }
}
</script>

<style scoped>
.gx-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.gx-canvas--fixed {
  position: fixed;
}
</style>
